"use client";

import Image from "next/image";
import { ArrowRight } from "lucide-react";
import { useEffect, useState, type CSSProperties } from "react";

import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import type { FortnoxSubpageCopy } from "./fortnox-page-copy";

type CcFortnoxMapProps = {
  copy: FortnoxSubpageCopy["map"];
  fortnoxLogo: string;
};

const CYCLE_MS = 3600;

/**
 * "What is the Fortnox API": a plain definition for the search answer, then
 * an integration map with Fortnox in the middle and data packets travelling
 * along each line. The selected system's flow is explained under the map;
 * the selection moves by itself until the visitor picks one.
 */
export function CcFortnoxMap({ copy, fortnoxLogo }: CcFortnoxMapProps) {
  const reduced = useCcPrefersReducedMotion();
  const [active, setActive] = useState(0);
  const [picked, setPicked] = useState(false);
  const count = copy.nodes.length;
  const node = copy.nodes[active];

  useEffect(() => {
    if (reduced || picked) return;
    const timer = window.setTimeout(() => setActive((value) => (value + 1) % count), CYCLE_MS);
    return () => window.clearTimeout(timer);
  }, [active, picked, reduced, count]);

  const points = copy.nodes.map((_, index) => {
    const angle = (index / count) * Math.PI * 2 - Math.PI / 2;
    return { x: 50 + Math.cos(angle) * 40, y: 50 + Math.sin(angle) * 40 };
  });

  return (
    <section
      className="cc-section cc-fx-map"
      id="fortnox-map"
      aria-labelledby="cc-fx-map-title"
      data-cc-nav="light"
      {...devSectionAttr("fortnox-map")}
    >
      <div className="cc-fx-map__container">
        <div className="cc-fx-map__copy">
          <CcSanjayaEyebrow theme="on-subtle">{copy.eyebrow}</CcSanjayaEyebrow>
          <h2 id="cc-fx-map-title" className="cc-fx-map__title">
            {copy.title}
          </h2>
          <p className="cc-fx-map__text">{copy.text}</p>
          <span className="cc-fx-map__data-label">{copy.dataLabel}</span>
          <ul className="cc-fx-map__data">
            {copy.data.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="cc-fx-map__visual">
          <div className="cc-fx-orbit">
            <svg className="cc-fx-orbit__lines" viewBox="0 0 100 100" aria-hidden="true">
              {points.map((point, index) => {
                const path = `M50 50 L${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
                const isActive = index === active;
                return (
                  <g key={copy.nodes[index].id} data-active={isActive ? "true" : undefined}>
                    <path d={path} />
                    {reduced ? null : (
                      <circle r={isActive ? 1.3 : 0.8}>
                        <animateMotion
                          dur={isActive ? "1.4s" : "2.8s"}
                          repeatCount="indefinite"
                          path={index % 2 ? `M${point.x.toFixed(2)} ${point.y.toFixed(2)} L50 50` : path}
                          begin={`${(index * 0.35).toFixed(2)}s`}
                        />
                      </circle>
                    )}
                  </g>
                );
              })}
            </svg>
            <span className="cc-fx-orbit__core">
              <Image src={fortnoxLogo} alt="" width={34} height={34} unoptimized />
              <b>{copy.center}</b>
            </span>
            {copy.nodes.map((item, index) => (
              <button
                key={item.id}
                type="button"
                className="cc-fx-orbit__node"
                aria-pressed={index === active}
                aria-label={item.name}
                style={{ left: `${points[index].x}%`, top: `${points[index].y}%`, "--cc-fx-i": index } as CSSProperties}
                onClick={() => {
                  setPicked(true);
                  setActive(index);
                }}
              >
                <Image
                  src={item.logo}
                  alt=""
                  width={"wide" in item && item.wide ? 34 : 24}
                  height={24}
                  unoptimized
                />
              </button>
            ))}
          </div>

          <div className="cc-fx-map__panel" aria-live="polite">
            <span className="cc-fx-map__hint">{copy.hint}</span>
            <strong>
              {node.name}
              <ArrowRight size={14} strokeWidth={2.4} aria-hidden="true" />
              {copy.center}
            </strong>
            <em>{node.flow}</em>
            <p>{node.text}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
